<div class="task-progress-container">
  <!-- Active Tasks -->
  @for (task of asyncTaskService.activeTasks(); track task.task_id) {
    <div class="task-card task-active">
      <div class="task-header">
        <div class="task-title">
          <span class="task-icon">
            @if (task.status === 'pending') {
              <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/>
                <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" stroke-width="4" fill="none" stroke-linecap="round"/>
              </svg>
            }
            @if (task.status === 'processing') {
              <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" opacity="0.25"/>
                <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" stroke-width="4" fill="none" stroke-linecap="round"/>
              </svg>
            }
          </span>
          <span class="task-type">{{ task.displayLabel }}</span>
        </div>
        <button class="btn-close" (click)="removeTask(task.task_id)" type="button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- task.step is what the backend actually reports ("Deleted 3 of 12", "Converting
           video"); getStatusText is the generic fallback for a task that sends no step. -->
      <div class="task-status">
        <span class="status-text">{{ task.step || getStatusText(task.status) }}</span>
        <span class="task-meta">
          <span class="task-duration">{{ getTaskDuration(task) }}</span>
          @if (task.progress > 0) {
            <span class="progress-text">{{ task.progress }}%</span>
          }
        </span>
      </div>

      <div class="progress-bar">
        @if (task.progress > 0) {
          <div class="progress-fill" [style.width.%]="task.progress"></div>
        } @else {
          <div class="progress-fill indeterminate"></div>
        }
      </div>
    </div>
  }

  <!-- Completed Tasks -->
  @for (task of asyncTaskService.completedTasks(); track task.task_id) {
    <div class="task-card task-completed">
      <div class="task-header">
        <div class="task-title">
          <span class="task-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </span>
          <span class="task-type">{{ task.displayLabel }}</span>
        </div>
        <button class="btn-close" (click)="removeTask(task.task_id)" type="button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="task-status">
        <span class="status-text">{{ getStatusText(task.status) }}</span>
        <span class="task-meta">
          <span class="task-duration">{{ getTaskDuration(task) }}</span>
        </span>
      </div>

      @if (getDeleteSummary(task); as summary) {
        <div class="task-note" [title]="summary">{{ summary }}</div>
      }

      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>

      @if (task.type === 'contact-import' && task.result) {
        <div class="task-actions-row items-center justify-center p-4">
          @if ((task.result.errors ?? 0) > 0) {
            <cm-ui-standard-button rounded icon="download" (onClick)="handleDownloadErrors(task)">
              {{ 'async_task.action.downloadErrors' | translate }}
            </cm-ui-standard-button>
          }
          @if (task.result.logDownloadUrl) {
            <cm-ui-standard-button rounded icon="description" (onClick)="handleDownloadLog(task)">
              {{ 'async_task.action.downloadLog' | translate }}
            </cm-ui-standard-button>
          }
        </div>
      } @else if (hasActionableResult(task)) {
        <div class="task-actions-row items-center justify-center p-4">
          <cm-ui-standard-button
            rounded
            [icon]="getActionIcon(task, 'content_copy')"
            (onClick)="handleAction(task)"
          >
            {{ getActionLabel(task) }}
          </cm-ui-standard-button>
        </div>
      }
    </div>
  }

  <!-- Failed Tasks -->
  @for (task of asyncTaskService.failedTasks(); track task.task_id) {
    <div class="task-card task-failed">
      <div class="task-header">
        <div class="task-title">
          <span class="task-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
          </span>
          <span class="task-type">{{ task.displayLabel }}</span>
        </div>
        <button class="btn-close" (click)="removeTask(task.task_id)" type="button">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div class="task-status">
        <span class="status-text">{{ getStatusText(task.status) }}</span>
        <span class="task-meta">
          <span class="task-duration">{{ getTaskDuration(task) }}</span>
        </span>
      </div>

      @if (task.error) {
        <div class="error-text" [title]="task.error">{{ task.error }}</div>
      }

      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>

      @if (task.type === 'contact-import' && task.result) {
        <div class="task-actions-row items-center justify-center p-4">
          @if ((task.result.errors ?? 0) > 0) {
            <cm-ui-standard-button rounded icon="download" (onClick)="handleDownloadErrors(task)">
              {{ 'async_task.action.downloadErrors' | translate }}
            </cm-ui-standard-button>
          }
          @if (task.result.logDownloadUrl) {
            <cm-ui-standard-button rounded icon="description" (onClick)="handleDownloadLog(task)">
              {{ 'async_task.action.downloadLog' | translate }}
            </cm-ui-standard-button>
          }
        </div>
      } @else if (hasActionableResult(task)) {
        <div class="task-actions-row items-center justify-center p-4">
          <cm-ui-standard-button rounded [icon]="getActionIcon(task, 'description')" (onClick)="handleAction(task)">
            {{ getActionLabel(task) }}
          </cm-ui-standard-button>
        </div>
      }
    </div>
  }

  <!-- Clear All Buttons -->
  @if (asyncTaskService.completedTasks().length > 0 || asyncTaskService.failedTasks().length > 0) {
    <div class="task-actions">
      @if (asyncTaskService.completedTasks().length > 0) {
        <cm-ui-standard-button rounded (onClick)="clearCompleted()">
          {{ 'async_task.clear_completed' | translate }}
        </cm-ui-standard-button>
      }
      @if (asyncTaskService.failedTasks().length > 0) {
        <cm-ui-standard-button rounded (onClick)="clearFailed()">
          {{ 'async_task.clear_failed' | translate }}
        </cm-ui-standard-button>
      }
    </div>
  }
</div>
